import { JSX, useCallback, useEffect, useState } from 'react';
import {
    getConversationalSearchContentClass,
    SearchFilterContentTypes,
    SearchOption,
    SearchOrigins,
} from 'search-gateway-client';
import { useStatsigExperimentContext } from 'statsig/components';
import { twMerge } from 'tailwind-merge';

import { Text } from '@videoblocks/design-system';

import { NavSearchContext, NavSearchContextProvider, NavSearchProps } from '../context/NavSearchContext';
import { useSearchWithAI } from '../hooks/useSearchWithAI';
import { CrossAssetNavSuggestions } from '../types/shared';
import getConversationalSearchUrl from '../utils/getConversationalSearchUrl';
import { useMedia } from '../utils/useMedia';
import { saveSearchHistory } from './AutosuggestElements/utils';
import CrossAssetNavSearchBox from './NavSearchElements/CrossAssetNavSearchBox';
import NavSearchBox from './NavSearchElements/NavSearchBox';
import { cleanSearchKeywords, SearchBarVariant } from './NavSearchElements/searchUtils';
import ProminentSearchButton from './ProminentSearchElements/ProminentSearchButton';
import ProminentSearchDropdown from './ProminentSearchElements/ProminentSearchDropdown';
import RightArrowButton from './ProminentSearchElements/RightArrowButton';
import SearchWithAIToggle from './ProminentSearchElements/SearchWithAIToggle';
import SearchWithAudio from './SearchWithAudioButton';

export interface ProminentSearchProps extends NavSearchProps {
    onDropdownChange: (contentType: string) => void;
    searchTerm: string;
    prominentSearchOptions: SearchOption[];
    contentTypeFromPrevious: SearchFilterContentTypes;
    showDropDown: boolean;
    showContentToggles?: boolean;
    searchGatewayFetch?: boolean;
    warpPage?: boolean;
    aiCtaDisabledForContentType?: boolean;
    isLanding?: boolean;
}

export default function ProminentSearch(props: ProminentSearchProps): JSX.Element {
    const {
        onDropdownChange,
        onSubmit,
        userHasAnySubscription,
        prominentSearchOptions,
        contentTypeFromPrevious,
        showDropDown,
        showContentToggles = false,
        useSnapshots = false,
        searchGatewayFetch = false,
        warpPage = false,
        aiCtaDisabledForContentType = false,
        isLanding = false,
    } = props;

    const defaultSearchOption = prominentSearchOptions?.[0]?.id ?? contentTypeFromPrevious;

    const [contentType, setContentType] = useState(contentTypeFromPrevious ?? defaultSearchOption);
    const [searchTerm, setSearchTerm] = useState(props.searchTerm ?? '');
    const { isSearchHistoryEnabled, showConversationalSearchCta } = useStatsigExperimentContext();
    const { conversationalSearchEnabled, getPlaceholder } = useSearchWithAI({ showConversationalSearchCta });

    // Ensure searchTerm stays up to date when given a new one via props.
    useEffect(() => {
        setSearchTerm(props.searchTerm ?? '');
    }, [props.searchTerm]);

    useEffect(() => {
        setContentType(contentTypeFromPrevious);
    }, [contentTypeFromPrevious]);

    // i.e. tailwind md breakpoint https://storywind.netlify.app/?path=/story/design-grids--page#medium-672-1024px
    const isMediumScreen = useMedia('(min-width: 672px)', true);

    const handleDropdownChange = (contentType: SearchFilterContentTypes) => {
        onDropdownChange(contentType);
        setContentType(contentType);
    };

    const makeEmptySuggestions = (): CrossAssetNavSuggestions => ({
        keywords: [],
        relatedCollections: [],
        latestCollections: [],
        trendingCollections: [],
        relatedContentTypesAndKeywords: [],
    });

    const [suggestions, setSuggestions] = useState(makeEmptySuggestions());

    const clearSuggestions = useCallback(() => setSuggestions(makeEmptySuggestions()), []);

    const goToConversationalSearch = (
        contentTypeSearched: SearchFilterContentTypes,
        cleanedSearchKeywords: string,
        searchOrigin: SearchOrigins,
    ): void => {
        // https://github.com/facebook/react/issues/35158
        // eslint-disable-next-line react-hooks/immutability
        window.location.href = getConversationalSearchUrl(
            cleanedSearchKeywords,
            searchOrigin,
            getConversationalSearchContentClass(contentTypeSearched),
        );
    };

    const handleSubmit = (
        contentTypeSearched: SearchFilterContentTypes = contentType,
        value: string = searchTerm,
        searchOriginOverride?: SearchOrigins,
    ) => {
        const cleanedSearchKeywords = cleanSearchKeywords(value);
        const searchOrigin = searchOriginOverride ?? SearchOrigins.SEARCH_BAR;

        if (isSearchHistoryEnabled) {
            saveSearchHistory(contentTypeSearched, cleanedSearchKeywords);
        }

        if (conversationalSearchEnabled) {
            goToConversationalSearch(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
            return;
        }

        onSubmit(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
    };

    const activeTab = `${isLanding ? 'bg-blue-600 text-white' : 'bg-white text-gray-800'} thick-3 offset-5`;
    const inactiveTab = isLanding ? 'text-gray-800 bg-white border border-gray-300' : 'text-white bg-black/15';

    return (
        <NavSearchContextProvider {...props}>
            <NavSearchContext.Consumer>
                {({ setShowSuggestions, searchBarVariant }) => (
                    <div
                        className={twMerge(
                            'flex flex-col w-full font-inter text-gray-900',
                            searchBarVariant === SearchBarVariant.TopNavSearch
                                ? 'rounded-full border border-solid border-gray-400'
                                : '',
                            !isLanding && 'items-center',
                        )}
                    >
                        {showContentToggles && (
                            <div
                                className={twMerge(
                                    isLanding ? 'flex flex-wrap' : 'md:pb-6 lg:pb-6',
                                    'product m-0 flex text-base font-bold grid-rows-4 pb-4 md:pb-6 lg:pb-6 cursor-pointer gap-3',
                                )}
                            >
                                {prominentSearchOptions.map((link) => (
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        key={link.id}
                                        className={twMerge(
                                            'text-base rounded-sm px-2 py-1 border-0',
                                            contentType === link.id ? activeTab : inactiveTab,
                                        )}
                                        data-value={link.id}
                                        onClick={() => handleDropdownChange(link.id)}
                                        onKeyDown={(event) => event.key === 'Enter' && handleDropdownChange(link.id)}
                                    >
                                        <Text fontWeight="semibold" asChild>
                                            <span>{link.label}</span>
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex items-center justify-center gap-4">
                            <form
                                className={twMerge(
                                    'border-none rounded-full flex items-center bg-white',
                                    isLanding
                                        ? 'w-full border border-solid border-gray-400'
                                        : 'lg:w-[822px] md:w-[480px]',
                                    searchBarVariant === SearchBarVariant.TopNavSearch ? '' : 'pl-5',
                                    showConversationalSearchCta && 'pl-0',
                                )}
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    setShowSuggestions(false);
                                    handleSubmit(contentType, searchTerm);
                                }}
                            >
                                {showDropDown && (
                                    <>
                                        <ProminentSearchDropdown
                                            onChange={handleDropdownChange}
                                            value={contentType}
                                            useSnapshots={useSnapshots}
                                            clearSuggestions={clearSuggestions}
                                            useNewSearchUI={searchBarVariant === SearchBarVariant.TopNavSearch}
                                        />
                                        {searchBarVariant !== SearchBarVariant.TopNavSearch && (
                                            <div>
                                                <div className="w-px h-11 bg-gray-500" />
                                            </div>
                                        )}
                                    </>
                                )}
                                {showConversationalSearchCta && (
                                    <SearchWithAIToggle disabledForContentType={aiCtaDisabledForContentType} />
                                )}
                                {isMediumScreen ? (
                                    <CrossAssetNavSearchBox
                                        contentType={contentType}
                                        setSearchTerm={setSearchTerm}
                                        onSubmit={handleSubmit}
                                        searchTerm={searchTerm}
                                        suggestions={suggestions}
                                        setSuggestions={setSuggestions}
                                        clearSuggestions={clearSuggestions}
                                        placeholder={getPlaceholder(
                                            prominentSearchOptions.find(({ id }) => id === contentType)
                                                ?.placeholderText,
                                            contentType,
                                        )}
                                        searchGatewayFetch={searchGatewayFetch}
                                        isProminentSearch
                                        useWarpSearchUI={warpPage}
                                    />
                                ) : (
                                    <NavSearchBox
                                        contentType={contentType}
                                        onChange={setSearchTerm}
                                        onSubmit={handleSubmit}
                                        searchTerm={searchTerm}
                                        placeholder={getPlaceholder(
                                            prominentSearchOptions.find(({ id }) => id === contentType)
                                                ?.placeholderText,
                                            contentType,
                                        )}
                                        userHasAnySubscription={userHasAnySubscription}
                                        isProminentSearch
                                        useWarpSearchUI={warpPage}
                                    />
                                )}
                                {showConversationalSearchCta ? <RightArrowButton /> : <ProminentSearchButton />}
                            </form>
                            <SearchWithAudio contentType={contentType} />
                        </div>
                    </div>
                )}
            </NavSearchContext.Consumer>
        </NavSearchContextProvider>
    );
}
